import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Sparkles } from "lucide-react";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "partner" ? "partner" : "customer");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", city: "Bangalore" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await register({ ...form, role });
      navigate(u.role === "partner" ? "/partner" : "/customer");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#1E3A8A] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="font-display font-black text-2xl">Dhobi</div>
        </Link>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-border">
          <h1 className="font-display font-bold text-3xl mb-2">Create account</h1>
          <p className="text-muted-foreground mb-6">Join Dhobi today.</p>

          <div className="grid grid-cols-2 gap-2 mb-6 p-1 bg-secondary rounded-xl">
            <button
              type="button"
              onClick={() => setRole("customer")}
              className={`py-2.5 rounded-lg font-semibold text-sm transition-all ${role === "customer" ? "bg-white text-[#1E3A8A] shadow-sm" : "text-muted-foreground"}`}
              data-testid="register-role-customer"
            >
              I'm a Customer
            </button>
            <button
              type="button"
              onClick={() => setRole("partner")}
              className={`py-2.5 rounded-lg font-semibold text-sm transition-all ${role === "partner" ? "bg-white text-[#C2410C] shadow-sm" : "text-muted-foreground"}`}
              data-testid="register-role-partner"
            >
              I'm a Laundry Owner
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg border border-border focus:ring-2 focus:ring-[#1E3A8A] outline-none"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg border border-border focus:ring-2 focus:ring-[#1E3A8A] outline-none"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-border focus:ring-2 focus:ring-[#1E3A8A] outline-none"
                placeholder="+91 98xxxxxxxx"
                data-testid="register-phone-input"
              />
            </div>
            <div>
              <label className="text-sm font-semibold mb-1.5 block">City</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-border focus:ring-2 focus:ring-[#1E3A8A] outline-none"
                data-testid="register-city-input"
              />
            </div>
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Password</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                required
                minLength={6}
                className="w-full px-4 py-3 rounded-lg border border-border focus:ring-2 focus:ring-[#1E3A8A] outline-none"
                data-testid="register-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3.5 rounded-lg text-white font-bold disabled:opacity-60 ${role === "partner" ? "bg-[#C2410C] tactile-btn-accent" : "bg-[#1E3A8A] tactile-btn"}`}
              data-testid="register-submit-button"
            >
              {loading ? "Creating account..." : `Create ${role === "partner" ? "Partner" : "Customer"} Account`}
            </button>
          </form>

          <p className="text-center mt-6 text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-[#1E3A8A] font-semibold hover:underline" data-testid="register-to-login-link">
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
